import React, {useEffect, useMemo, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {Avatar as PrimeAvatar} from "primereact/avatar";
import {Button} from "primereact/button";
import {DataView} from "primereact/dataview";
import {InputSwitch} from "primereact/inputswitch";
import {ListBox} from "primereact/listbox";
import {Message} from "primereact/message";
import {OverlayPanel} from "primereact/overlaypanel";
import {ProgressSpinner} from "primereact/progressspinner";
import {Tag} from "primereact/tag";
import {useUserAccess} from "../../../models/user-access/use-user-access";
import Avatar from "../../../system/components/avatar";
import Icon from "../../../system/components/icon";
import {useTranslate} from "../../../system/useTranslate";
import {setWorkspaceMembership} from "../../../system/app-slice";
import MultiSelectMembers from "../../tasks/components/multi-select-members";
import {
    normalizeWorkspaceAdminPermissions,
    setWorkspaceAdminPermissionGroup,
    toggleWorkspaceAdminPermission,
    WORKSPACE_ADMIN_PERMISSION_GROUPS,
} from "../../../system/workspace-admin-permissions";

const entityKey = (entity) => entity?.documentId || entity?.id;
const entityKeys = (entity) => [entity?.documentId, entity?.id]
    .filter((value) => value !== null && value !== undefined)
    .map(String);
const sameEntity = (left, right) => entityKeys(left).some((key) => entityKeys(right).includes(key));
const sameMembership = (left, right) => (
    sameEntity(left, right)
    || sameEntity(left?.member, right?.member)
);
const roleMember = (role) => role?.workspace_membership?.member;

const membershipForRole = (role, workspaceMemberships = []) => {
    const roleMembership = role?.workspace_membership;
    return workspaceMemberships.find((membership) => sameMembership(membership, roleMembership))
        || roleMembership
        || null;
};

const replaceMembershipWorkspaceRole = (membership, nextRole) => {
    if (!sameMembership(membership, nextRole?.workspace_membership)) return membership;
    const workspaceRole = {...nextRole};
    delete workspaceRole.workspace_membership;

    return {
        ...membership,
        user_roles: [
            ...(membership?.user_roles || []).filter((role) => role?.scope_type !== "workspace"),
            workspaceRole,
        ],
    };
};

function AccessControl() {
    const translate = useTranslate();
    const userAccess = useUserAccess();
    const dispatch = useDispatch();
    const {workspaceMembershipData} = useSelector((state) => state.app);
    const workspace = workspaceMembershipData?.workspace;
    const workspaceMemberships = useMemo(
        () => workspace?.workspace_memberships || [],
        [workspace?.workspace_memberships],
    );
    const [currentTab, setCurrentTab] = useState("owner");
    const [roles, setRoles] = useState([]);
    const [selectedRole, setSelectedRole] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);
    const [draftPermissions, setDraftPermissions] = useState([]);
    const [permissionsDirty, setPermissionsDirty] = useState(false);

    const visibleRoles = useMemo(
        () => roles.filter((role) => role?.role === currentTab && role?.scope_type === "workspace"),
        [roles, currentTab],
    );
    const selectedAdminMemberships = useMemo(
        () => roles
            .filter((role) => role?.role === "admin" && role?.scope_type === "workspace")
            .map((role) => membershipForRole(role, workspaceMemberships))
            .filter(Boolean),
        [roles, workspaceMemberships],
    );

    useEffect(() => {
        let active = true;

        const loadRoles = async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await userAccess.getWorkspaceAccessRoles();
                if (active) setRoles(data);
            } catch (requestError) {
                if (active) setError(requestError);
            } finally {
                if (active) setLoading(false);
            }
        };

        if (workspace?.documentId) loadRoles();
        return () => {
            active = false;
        };
        // The active workspace identifier is the request boundary. The model hook
        // exposes new function references on every render and must not retrigger it.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [workspace?.documentId, reloadKey]);

    useEffect(() => {
        const selectedKey = entityKey(selectedRole);
        const stillVisible = visibleRoles.find((role) => String(entityKey(role)) === String(selectedKey));
        setSelectedRole(stillVisible || visibleRoles[0] || null);
    }, [visibleRoles, selectedRole]);

    useEffect(() => {
        setDraftPermissions(normalizeWorkspaceAdminPermissions(selectedRole?.permissions));
        setPermissionsDirty(false);
    }, [selectedRole]);

    const errorMessage = error?.message
        ? translate(error.message)
        : translate("access-control.error.generic");

    const replaceRole = (nextRole) => {
        setRoles((current) => current.map((role) => (
            String(entityKey(role)) === String(entityKey(nextRole)) ? nextRole : role
        )));
        setSelectedRole(nextRole);
    };

    const syncMembershipRole = (nextRole) => {
        if (!nextRole?.workspace_membership || !workspaceMembershipData?.workspace) return;

        const nextActiveMembership = replaceMembershipWorkspaceRole(
            workspaceMembershipData,
            nextRole,
        );
        dispatch(setWorkspaceMembership({
            ...nextActiveMembership,
            workspace: {
                ...workspaceMembershipData.workspace,
                workspace_memberships: workspaceMemberships.map((membership) => (
                    replaceMembershipWorkspaceRole(membership, nextRole)
                )),
            },
        }));
    };

    const savePermissions = async () => {
        if (!selectedRole || selectedRole.role !== "admin" || saving || !permissionsDirty) return;
        setSaving(true);
        setError(null);
        try {
            const updated = await userAccess.updateWorkspaceAdmin(
                entityKey(selectedRole),
                draftPermissions,
            );
            replaceRole(updated);
            syncMembershipRole(updated);
            setDraftPermissions(normalizeWorkspaceAdminPermissions(updated.permissions));
            setPermissionsDirty(false);
        } catch (requestError) {
            setError(requestError);
        } finally {
            setSaving(false);
        }
    };

    const updatePermission = (permission, enabled) => {
        if (!selectedRole || selectedRole.role !== "admin" || saving) return;
        setDraftPermissions((current) => (
            toggleWorkspaceAdminPermission(current, permission, enabled)
        ));
        setPermissionsDirty(true);
    };

    const updatePermissionGroup = (groupId, enabled) => {
        if (!selectedRole || selectedRole.role !== "admin" || saving) return;
        setDraftPermissions((current) => (
            setWorkspaceAdminPermissionGroup(current, groupId, enabled)
        ));
        setPermissionsDirty(true);
    };

    const updateAdminMembership = async ({member, status}) => {
        if (!member || saving) return;
        if (status === "added" && !canSelectAsAdmin(member)) return;
        if (
            status === "added"
            && ["restricted", "removed"].includes(member?.memberShipStatus)
        ) return;

        setSaving(true);
        setError(null);
        try {
            if (status === "added") {
                const promoted = await userAccess.promoteWorkspaceAdmin(entityKey(member), []);
                setRoles((current) => [
                    ...current.filter((role) => String(entityKey(role)) !== String(entityKey(promoted))),
                    promoted,
                ]);
                setSelectedRole(promoted);
                syncMembershipRole(promoted);
            } else if (status === "removed") {
                const role = roles.find((item) => (
                    item.role === "admin"
                    && sameMembership(item.workspace_membership, member)
                ));
                if (role) {
                    const demoted = await userAccess.demoteWorkspaceAdmin(entityKey(role));
                    setRoles((current) => current.filter((item) => (
                        String(entityKey(item)) !== String(entityKey(role))
                    )));
                    syncMembershipRole(demoted);
                }
            }
        } catch (requestError) {
            setError(requestError);
        } finally {
            setSaving(false);
        }
    };

    const ownerRole = roles.find((role) => role?.role === "owner" && role?.scope_type === "workspace");
    const owner = membershipForRole(ownerRole, workspaceMemberships)
        || workspace?.owner;
    const ownerMember = owner?.member || roleMember(ownerRole) || workspace?.owner;
    const canSelectAsAdmin = (membership) => {
        if (!membership?.member) return false;
        if (owner && sameMembership(membership, owner)) return false;
        return !ownerMember || !sameEntity(membership.member, ownerMember);
    };

    return (
        <div className="min-h-full w-full flex flex-column gap-3 p-3">
            <div className="flex flex-column align-items-center justify-content-center gap-3 p-2 text-center">
                <h2 className="text-4xl font-bold m-0">{translate("roles-and-access-levels")}</h2>
                <p className="m-0 text-400 text-xs">{translate("roles-and-access-levels-description")}</p>
                <RoleTabs value={currentTab} onSelected={setCurrentTab}/>
            </div>

            {error && (
                <div className="flex flex-column sm:flex-row align-items-stretch sm:align-items-center gap-2">
                    <Message
                        severity="error"
                        className="flex-1 justify-content-start text-xs"
                        text={errorMessage}
                    />
                    <Button
                        type="button"
                        outlined
                        severity="secondary"
                        className="text-xs gap-2"
                        icon={<Icon icon="mi mi:refresh"/>}
                        label={translate("access-control.retry")}
                        onClick={() => setReloadKey((current) => current + 1)}
                        disabled={loading || saving}
                    />
                </div>
            )}

            <div className="flex flex-column lg:flex-row flex-1 w-full shadow-1 border-round-xl surface-card">
                <RoleMembers
                    currentTab={currentTab}
                    value={selectedRole}
                    options={visibleRoles}
                    loading={loading}
                    saving={saving}
                    canSelectAsAdmin={canSelectAsAdmin}
                    workspaceMemberships={workspaceMemberships}
                    selectedAdminMemberships={selectedAdminMemberships}
                    onSelect={setSelectedRole}
                    onChange={updateAdminMembership}
                />
                <Permissions
                    currentTab={currentTab}
                    userRoleData={selectedRole}
                    enabledPermissions={draftPermissions}
                    dirty={permissionsDirty}
                    saving={saving}
                    onChange={updatePermission}
                    onGroupChange={updatePermissionGroup}
                    onSave={savePermissions}
                />
            </div>
        </div>
    );
}

function RoleMembers({
    currentTab,
    value,
    options,
    loading,
    saving,
    canSelectAsAdmin,
    workspaceMemberships,
    selectedAdminMemberships,
    onSelect,
    onChange,
}) {
    const translate = useTranslate();
    const overlayRef = useRef();

    const itemTemplate = (role) => {

        const member = role?.workspace_membership?.member;
        return (
            <div className="flex gap-2 align-items-center text-xs">
                <Avatar memberData={member}/>
                <span>{member?.fullname}</span>
            </div>
        );
    };

    return (
        <section className="w-full lg:w-3 surface-50 flex flex-column border-bottom-1 lg:border-bottom-none lg:border-left-1 border-100">
            <span className="p-3 font-bold">
                {translate("members-of-role").replace("{{ROLE}}", translate(currentTab))}
            </span>
            {currentTab === "admin" && (
                <div className="px-2 pb-2">
                    <Button
                        type="button"
                        onClick={(event) => overlayRef.current?.toggle(event)}
                        disabled={saving || loading}
                        className="w-full p-button-outlined p-button-secondary shadow-none text-xs p-2 gap-2"
                        icon={<Icon icon="mi mi:person_add"/>}
                        label={translate("add-new-user-role")}
                    />
                    <OverlayPanel
                        pt={{root: {className: "p-0 rtl"}, content: {className: "p-0"}}}
                        ref={overlayRef}
                    >
                        <MultiSelectMembers
                            onChange={onChange}
                            options={workspaceMemberships.filter(canSelectAsAdmin)}
                            value={selectedAdminMemberships}
                            disabled={(membership) => (
                                ["restricted", "removed"].includes(membership?.memberShipStatus)
                                && !selectedAdminMemberships.some((selected) => (
                                    sameMembership(selected, membership)
                                ))
                            )}
                        />
                    </OverlayPanel>
                </div>
            )}
            {loading ? (
                <div className="flex flex-1 align-items-center justify-content-center p-4">
                    <ProgressSpinner className="w-2rem h-2rem" strokeWidth="5"/>
                </div>
            ) : (
                <ListBox
                    dataKey="id"
                    pt={{item: ({context}) => ({className: context.selected ? "border-right-3" : ""})}}
                    value={value}
                    onChange={(event) => onSelect(event.value)}
                    itemTemplate={itemTemplate}
                    options={options}
                    className="border-none border-noround"
                    emptyMessage={translate(currentTab === "owner" ? "access-control.owner-missing" : "access-control.admins-empty")}
                />
            )}
        </section>
    );
}

function RoleTabs({value, onSelected}) {
    const translate = useTranslate();
    const cards = [
        {label: "owner", description: "role-owner-description", icon: "mi mi:stars"},
        {label: "admin", description: "role-admin-description", icon: "mi mi:admin_panel_settings"},
    ];

    const itemTemplate = (data) => {
        const selected = value === data.label;
        return (
            <div className="p-2 flex-1 w-full">
                <Button
                    aria-pressed={selected}
                    onClick={() => onSelected(data.label)}
                    className={`appearance-none surface-card h-full w-full text-xs shadow-1 border-round-xl cursor-pointer flex flex-column align-items-center justify-content-center gap-2 p-3 ${selected ? "border-2 border-primary-500 bg-primary-50" : "border-1 border-100"}`}
                >
                    <PrimeAvatar
                        size="large"
                        className="bg-primary-100 text-primary-500 border-round-xl"
                        icon={<Icon icon={data.icon}/>}
                    />
                    <span className="font-bold">{translate(data.label)}</span>
                    <span className="text-400">{translate(data.description)}</span>
                </Button>
            </div>
        );
    };

    return (
        <DataView
            className="w-full lg:w-6"
            layout="grid"
            itemTemplate={itemTemplate}
            value={cards}
        />
    );
}

function Permissions({
    currentTab,
    userRoleData,
    enabledPermissions,
    dirty,
    saving,
    onChange,
    onGroupChange,
    onSave,
}) {
    const translate = useTranslate();
    const readOnly = currentTab === "owner" || !userRoleData || saving;
    const member = roleMember(userRoleData);

    const itemTemplate = (permission) => {
        const enabled = currentTab === "owner" || enabledPermissions.includes(permission.key);
        return (
            <div className="w-full p-2">
                <div className={`surface-100 border-round align-items-center flex p-3 justify-content-between gap-3 ${enabled ? "border-1 border-primary-100" : "border-1 border-transparent"}`}>
                    <div className="flex align-items-center gap-2">
                        <PrimeAvatar icon={<Icon icon={permission.icon} className="text-400 text-2xl"/>}/>
                        <div className="flex flex-column text-xs">
                            <div className="flex align-items-center gap-2">
                                <span className="font-bold">{translate(permission.labelKey)}</span>
                                {permission.dangerous && (
                                    <Tag
                                        severity="danger"
                                        value={translate("access-control.dangerous")}
                                        className="text-2xs"
                                    />
                                )}
                            </div>
                            <span className="text-400 line-height-3">
                                {translate(permission.descriptionKey)}
                            </span>
                        </div>
                    </div>
                    <InputSwitch
                        aria-label={translate(permission.labelKey)}
                        checked={enabled}
                        disabled={readOnly}
                        onChange={(event) => onChange(permission.key, event.value)}
                    />
                </div>
            </div>
        );
    };

    return (
        <section className="flex flex-column w-full">
            <div className="flex flex-column p-3 border-bottom-1 border-100">
                <span className="font-bold">
                    {translate(currentTab === "owner" ? "owner-permissions" : "admin-permissions")}
                    {member?.fullname ? `: ${member.fullname}` : ""}
                </span>
                <span className="text-400 text-xs">
                    {translate(currentTab === "owner" ? "owner-permissions-description" : "admin-permissions-description")}
                </span>
            </div>
            {!userRoleData ? (
                <div className="flex flex-1 align-items-start justify-content-start p-3">
                    <Message
                        severity="info"
                        className="w-full justify-content-start gap-2 text-xs"
                        text={translate("access-control.select-role")}
                    />
                </div>
            ) : (
                <>
                    <div className="flex-1 overflow-auto p-2">
                        {WORKSPACE_ADMIN_PERMISSION_GROUPS.map((group) => {
                            const allEnabled = currentTab === "owner"
                                || group.permissions.every((permission) => (
                                    enabledPermissions.includes(permission.key)
                                ));
                            return (
                                <section key={group.id} className="mb-3 surface-card border-1 border-100 border-round-xl overflow-hidden">
                                    <div className="flex flex-column sm:flex-row sm:align-items-center justify-content-between gap-2 p-3 border-bottom-1 border-100 surface-50">
                                        <div className="flex flex-column gap-1">
                                            <span className="font-bold text-sm">{translate(group.labelKey)}</span>
                                            <span className="text-xs text-400">{translate(group.descriptionKey)}</span>
                                        </div>
                                        {currentTab === "admin" && (
                                            <Button
                                                type="button"
                                                text
                                                severity="secondary"
                                                className="text-xs"
                                                disabled={readOnly}
                                                label={translate(allEnabled
                                                    ? "access-control.group.clear-all"
                                                    : "access-control.group.select-all")}
                                                onClick={() => onGroupChange(group.id, !allEnabled)}
                                            />
                                        )}
                                    </div>
                                    <div className="grid m-0">
                                        {group.permissions.map((permission) => (
                                            <div key={permission.key} className="col-12 xl:col-6 p-0">
                                                {itemTemplate(permission)}
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            );
                        })}
                    </div>
                    {currentTab === "admin" && (
                        <div className="flex flex-column sm:flex-row align-items-stretch sm:align-items-center justify-content-between gap-2 p-3 border-top-1 border-100 surface-50">
                            <span className={`text-xs ${dirty ? "text-orange-600" : "text-400"}`}>
                                {translate(dirty
                                    ? "access-control.unsaved-changes"
                                    : "access-control.saved")}
                            </span>
                            <Button
                                type="button"
                                className="text-xs gap-2"
                                icon={<Icon icon="mi mi:save"/>}
                                label={translate("save-change")}
                                loading={saving}
                                disabled={readOnly || !dirty}
                                onClick={onSave}
                            />
                        </div>
                    )}
                </>
            )}
        </section>
    );
}

export default AccessControl;
